/**
 * Generated actions with review - the core V0 interaction.
 * Mini PRD: "Review UX: accept, reject, or tweak per action. Accepted
 * actions land in the Actions system... No action lands without explicit
 * carer acceptance."
 */
import { useEffect, useState } from 'react';
import { useNavigation } from '@/hooks/useNavigationContext';
import { Box, Stack, Typography, TextField } from '@mui/material';

import { Card } from '@karehero/llama/components/atoms/Card/Card';
import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import FlowScreen from './components/FlowScreen';
import { CheckIcon, XIcon, PencilIcon } from './components/LocalIcons';
import { palette } from './components/palette';
import { useCaptureFlow, type ReviewedAction } from './CaptureFlowContext';

const pillStyle = (active: boolean, activeColor: string) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  border: `1.5px solid ${active ? activeColor : palette.divider}`,
  backgroundColor: active ? activeColor : 'transparent',
  color: active ? '#fff' : palette.ink,
  borderRadius: 99,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
});

interface ActionCardProps {
  action: ReviewedAction;
  onDecide: (decision: 'accepted' | 'rejected' | 'pending') => void;
  onTweak: (title: string) => void;
}

const ActionCard = ({ action, onDecide, onTweak }: ActionCardProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(action.title);

  const isAccepted = action.decision === 'accepted';
  const isRejected = action.decision === 'rejected';

  return (
    <Card isFullWidth isPaddingSm>
      <Stack spacing={1.25} sx={{ opacity: isRejected ? 0.55 : 1 }}>
        {editing ? (
          <Stack spacing={1}>
            <TextField
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              size='small'
              multiline
              autoFocus
            />
            <Stack direction='row' spacing={1}>
              <Box
                component='button'
                onClick={() => {
                  onTweak(draft.trim() || action.title);
                  setEditing(false);
                }}
                sx={pillStyle(true, palette.forest)}
              >
                <CheckIcon size={14} /> Save
              </Box>
              <Box
                component='button'
                onClick={() => {
                  setDraft(action.title);
                  setEditing(false);
                }}
                sx={pillStyle(false, palette.divider)}
              >
                Cancel
              </Box>
            </Stack>
          </Stack>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: 15,
                textDecoration: isRejected ? 'line-through' : 'none',
              }}
            >
              {action.title}
              {action.isTweaked && (
                <Typography
                  component='span'
                  sx={{ color: palette.lavenderInk, fontSize: 12, ml: 1 }}
                >
                  edited
                </Typography>
              )}
            </Typography>
            <Typography sx={{ color: palette.mutedText, fontSize: 13 }}>
              {action.description}
            </Typography>
            <Stack direction='row' spacing={1}>
              <Box
                component='button'
                aria-label={`Accept: ${action.title}`}
                onClick={() => onDecide(isAccepted ? 'pending' : 'accepted')}
                sx={pillStyle(isAccepted, palette.forest)}
              >
                <CheckIcon size={14} /> {isAccepted ? 'Added' : 'Accept'}
              </Box>
              <Box
                component='button'
                aria-label={`Tweak: ${action.title}`}
                onClick={() => setEditing(true)}
                sx={pillStyle(false, palette.divider)}
              >
                <PencilIcon size={14} /> Tweak
              </Box>
              <Box
                component='button'
                aria-label={`Reject: ${action.title}`}
                onClick={() => onDecide(isRejected ? 'pending' : 'rejected')}
                sx={pillStyle(isRejected, palette.alertRed)}
              >
                <XIcon size={14} /> {isRejected ? 'Rejected' : 'Reject'}
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Card>
  );
};

export const ActionReview = () => {
  const navigate = useNavigation();
  const { doc, actions, decideAction, tweakAction, saveCapture } =
    useCaptureFlow();

  useEffect(() => {
    if (!doc) navigate('/capture', { replace: true });
  }, [doc, navigate]);

  if (!doc) return null;

  const acceptedCount = actions.filter((a) => a.decision === 'accepted').length;

  const saveAndFinish = (includeActions: boolean) => {
    saveCapture(includeActions);
    navigate(includeActions && acceptedCount > 0 ? '/actions' : '/');
  };

  return (
    <FlowScreen backTo='/capture/result'>
      <Stack spacing={2} sx={{ pb: 2 }}>
        <PageHeader
          title='Suggested next steps'
          description='Keni suggests these based on your document. Nothing is added until you accept it.'
        />

        {actions.map((action) => (
          <ActionCard
            key={action.id}
            action={action}
            onDecide={(decision) => decideAction(action.id, decision)}
            onTweak={(title) => tweakAction(action.id, title)}
          />
        ))}

        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Button
            ariaLabel='Save document and add accepted actions'
            variant='primary'
            isFullWidth
            isDisabled={acceptedCount === 0}
            onPress={() => saveAndFinish(true)}
          >
            {acceptedCount === 0
              ? 'Accept an action to continue'
              : `Save and add ${acceptedCount} action${acceptedCount === 1 ? '' : 's'}`}
          </Button>
          <Button
            ariaLabel='Save document without actions'
            variant='link'
            isFullWidth
            onPress={() => saveAndFinish(false)}
          >
            Save document without actions
          </Button>
        </Stack>
      </Stack>
    </FlowScreen>
  );
};

export default ActionReview;
